import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  fetchAuthSession,
  getCurrentUser,
  signOut,
  signInWithRedirect,
  type AuthUser,
} from 'aws-amplify/auth';
import { AuthContext, type AuthContextValue } from './AuthContext';
import { configureAuth, clearLegacyLocalStorageAuth } from './configureAuth';

configureAuth();

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function checkAuth() {
      try {
        const currentUser = await getCurrentUser();
        const session = await fetchAuthSession({ forceRefresh: false });
        const claims = session.tokens?.idToken?.payload;
        const portal = claims?.['custom:portal'];
        const role = claims?.['custom:role'];
        if (portal === 'admin' || role === 'admin' || role === 'user') {
          await signOut();
          if (!cancelled) setUser(null);
        } else if (!cancelled) {
          setUser(currentUser);
        }
      } catch {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    checkAuth();
    return () => { cancelled = true; };
  }, []);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    try {
      const session = await fetchAuthSession({ forceRefresh: false });
      return session.tokens?.accessToken?.toString() ?? null;
    } catch {
      return null;
    }
  }, []);

  const login = useCallback(async () => {
    await signInWithRedirect();
  }, []);

  const logout = useCallback(async () => {
    await signOut();
    clearLegacyLocalStorageAuth();
    setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      isAuthenticated: user !== null,
      getAccessToken,
      login,
      logout,
    }),
    [user, isLoading, getAccessToken, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
